import { IconInfo16, IconMap16 } from '@warp-ds/icons/react';
import { Affix, TextField } from '@warp-ds/react';
import cn from 'classnames';
import { type ChangeEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'ts-debounce';
import { TypingLoader } from '../../../../components/warp/typingLoader/TypingLoader';
import { useAutocompleteKeyDown } from '../../../../hooks/useAutocompleteKeyDown';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import type { AutocompleteOption, AutocompleteProps } from '../../../../types/index';
import { replaceDashWithComma } from '../../../../util/formatting';
import { FrontpageModal } from '../FrontpageModal';
import styles from './Autocomplete.module.css';
import { AutocompleteIcon } from './AutocompleteIcon';
import { AutocompleteOptionItem } from './AutocompleteOptionItem';

export const Autocomplete = ({
    id,
    label,
    defaultValue,
    placeholder,
    error,
    url,
    headers = {},
    mapOptions,
    onSelect,
    minChars = 1,
    vertical,
    departure,
    showDefaultOptionOnFocus = false,
}: AutocompleteProps) => {
    const [text, setText] = useState(defaultValue ?? '');
    const [selectedOption, setSelectedOption] = useState<AutocompleteOption | null>(null);
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const [selected, setSelected] = useState<number>(-1);
    const [showDropdown, setShowDropdown] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [noResults, setNoResults] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isSmallScreen = useBreakpoints({ breakpoint: 'sm' });
    const hasSelectedOption = options.length > 0 && selected !== -1;
    const showDefaultOption = showDefaultOptionOnFocus && text.length === 0 && showDropdown && !noResults;
    const showOptions = showDefaultOption || (showDropdown && (options.length > 0 || noResults));
    const selectedRef = useRef(selected);

    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    const DEFAULT_OPTION = useMemo<AutocompleteOption>(
        () => ({
            id: 'default',
            text: 'Hvor som helst',
            type: 'Default',
        }),
        [],
    );

    useEffect(() => setText(defaultValue ?? ''), [defaultValue]);

    const fetchCountRef = useRef(0);

    const fetchSuggestions = (value: string) => {
        const fetchNum = ++fetchCountRef.current;

        loaderTimeoutRef.current = setTimeout(() => {
            setShowLoader(true);
        }, 500);

        setFetching(true);

        fetch(`${url}${encodeURIComponent(value)}`, { headers })
            .then((res) => res.json())
            .then((json) => {
                if (fetchNum === fetchCountRef.current) {
                    const isValidArray = Array.isArray(json) && json.length > 0;
                    setOptions(isValidArray ? mapOptions(json) : []);
                    setNoResults(!isValidArray);
                }
            })
            .catch((err) => {
                console.error(err);
                setOptions([]);
                setNoResults(true);
            })
            .finally(() => {
                if (loaderTimeoutRef.current) {
                    clearTimeout(loaderTimeoutRef.current);
                    loaderTimeoutRef.current = null;
                }
                setShowLoader(false);
                setShowDropdown(true);
                setFetching(false);
                setSelected(0);
            });
    };

    const debouncedFetchOptions = useMemo(() => debounce(fetchSuggestions, 200), [url, mapOptions]);

    const handleInputChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setText(value);

        if (value.length === 0) {
            handleClear();
        } else {
            setSelectedOption(null);
            onSelect(null, value);
            if (value.length >= minChars) {
                debouncedFetchOptions(value);
            } else {
                setOptions([]);
                setNoResults(false);
            }
        }
    };

    const handleClear = () => {
        setText('');
        setSelectedOption(null);
        setSelected(-1);
        setOptions([]);
        setNoResults(false);
        onSelect(null);
    };

    const selectOption = (option: AutocompleteOption) => {
        setText(option.text);
        setSelectedOption(option);
        setShowDropdown(false);
        onSelect(option);
    };

    const selectCurrentOption = () => {
        if (selected === 0 && showDefaultOption) {
            selectOption(DEFAULT_OPTION);
        } else {
            const offset = showDefaultOption ? 1 : 0;
            const index = selected - offset;
            if (index >= 0 && index < options.length) {
                selectOption(options[index]);
            }
        }
    };

    const rawOnKeyDown = useAutocompleteKeyDown({
        options,
        selected,
        setSelected,
        setOptions,
        setNoResults,
        selectCurrentOption,
    });

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' && text.length === 0 && showDefaultOptionOnFocus) {
            setShowDropdown(true);
            setSelected(0);
            e.preventDefault();
            return;
        }

        if (e.key === 'Enter') {
            selectCurrentOption();
            e.preventDefault();
            return;
        }

        rawOnKeyDown(e);
    };

    const handleBlur = () => {
        if (!isSmallScreen) {
            setShowDropdown(false);

            if (selectedRef.current !== -1) {
                selectCurrentOption();
            }
        }
    };

    const renderListContent = () => {
        if (fetching && showLoader) {
            return <TypingLoader />;
        }

        if (showDefaultOption) {
            return (
                <>
                    <li role="presentation">
                        <strong className={styles['default-title']}>Utforsk</strong>
                    </li>
                    <AutocompleteOptionItem
                        id={id}
                        index={0}
                        option={DEFAULT_OPTION}
                        selected={selected}
                        vertical={vertical}
                        departure={departure}
                        onClick={() => selectOption(DEFAULT_OPTION)}
                        onHover={() => setSelected(0)}
                        icon={<IconMap16 aria-hidden />}
                        defaultView
                    />
                </>
            );
        }

        if (noResults) {
            return (
                <li className="flex flex-row items-center gap-6 p-6">
                    <IconInfo16 aria-hidden />
                    <span>Fant ingen resultater...</span>
                </li>
            );
        }

        return options.map((option, index) => {
            const adjustedIndex = showDefaultOption ? index + 1 : index;

            return (
                <AutocompleteOptionItem
                    key={`option-${option.id}`}
                    id={id}
                    index={adjustedIndex}
                    option={option}
                    selected={selected}
                    vertical={vertical}
                    departure={departure}
                    onClick={() => selectOption(option)}
                    onHover={() => setSelected(adjustedIndex)}
                    icon={
                        <AutocompleteIcon
                            className={styles['autocomplete-item__icon']}
                            option={option}
                            vertical={vertical}
                            departure={departure}
                        />
                    }
                />
            );
        });
    };

    const renderListbox = () => {
        return (
            showOptions && (
                <ul id={`${id}-options`} data-testid={`${id}-options`} className={styles['autocomplete-list']}>
                    {renderListContent()}
                </ul>
            )
        );
    };

    const renderInputGroup = (id: string) => (
        <div className={styles['input-group']}>
            <label htmlFor={id} className={styles.label}>
                {label}
            </label>
            <div className={styles['textfield-wrapper']}>
                <div
                    className={cn(styles.iconPrefix, {
                        [styles['iconPrefix--fullscreen']]: showDropdown,
                    })}>
                    <AutocompleteIcon option={selectedOption} vertical={vertical} departure={departure} />
                </div>
                <TextField
                    type="text"
                    id={id}
                    data-testid={id}
                    value={replaceDashWithComma(text)}
                    placeholder={placeholder ?? undefined}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onChange={handleInputChange}
                    onClick={() => setShowDropdown(true)}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-activedescendant={hasSelectedOption ? `${id}-option-${selected}` : undefined}
                    aria-controls={hasSelectedOption ? `${id}-options` : undefined}
                    aria-expanded={hasSelectedOption}
                    invalid={!!error}
                    helpText={error}
                    className={cn(styles.textfield, {
                        [styles['textfield--fullscreen']]: showDropdown,
                    })}>
                    {text?.length > 0 && <Affix suffix clear onClick={handleClear} aria-label="Nullstill" />}
                </TextField>
            </div>
        </div>
    );

    return (
        <>
            <FrontpageModal
                breakpoint="sm"
                title={placeholder}
                open={showDropdown}
                showFooter={false}
                onOpenChange={(setOpen) => {
                    setShowDropdown(setOpen);
                    if (!setOpen) {
                        setSelected(-1);
                        setOptions([]);
                        setNoResults(false);
                    }
                }}
                trigger={{
                    text: replaceDashWithComma(text) || placeholder,
                    prefix: <AutocompleteIcon option={selectedOption} vertical={vertical} departure={departure} />,
                }}
                hideChevron
                error={error}>
                {renderInputGroup(`${id}-mobile`)}
                {renderListbox()}
            </FrontpageModal>

            <div className={styles.autocomplete}>
                {renderInputGroup(`${id}-desktop`)}
                {renderListbox()}
            </div>
        </>
    );
};
